import type { IUserGrantAccessCmd } from "@/data/commands/user/UserGrantAccessCmd";
import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { UserDao } from "@/data/dao/service/UserDao";
import type { IUserAccessGrant } from "@/data/model/User";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { EntityInput } from "@/portal/formControls/EntityInput";
import { Select } from "@/portal/formControls/Select";
import { claimTypes } from "@/data/util/Config";

export class UserAccessWindow extends BaseModal {
    private $form: JQuery;

    private claimTypeSelect: Select<{ name: string; type: string }>;
    private locationSelect?: EntityInput;

    private $locationSelectionRow: JQuery;

    private $submitBtn: JQuery;

    private userId: string;
    private takenValues: Array<string>;

    public onSaved?: { (translation: IUserAccessGrant) };
    public onRemoved?: { () };

    constructor(userId: string, takenClaimValues: Array<string>) {
        super(i18n("modal.userAccess.title"), "modal-lg", true);

        this.userId = userId;
        this.takenValues = takenClaimValues;
        this.$element.addClass("acl");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.userAccess.grants") +
                "</h3></div></div>"
        );

        const claimTypeContainer = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.userAccess.kind") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const claimTypeSelect = $(
            '<select class="form-control">' +
            '<option value="http://tastier.ch/claims/locationgrant">' + i18n("modal.userAccess.editLocation") + "</option>" +
            "</select>"
        ).appendTo(claimTypeContainer);

        this.$locationSelectionRow = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' + i18n('general.entities.location') +' <span class="mandatory">*</span>' + '</label><div class="col-xl-6" /></div>'
        ).appendTo(createSection);

        this.claimTypeSelect = Select.createFromSelect(
            claimTypeSelect,
            undefined,
            (f) => f.name,
            undefined,
            undefined,
            "type",
            false
        );
        this.claimTypeSelect.setValue("http://tastier.ch/claims/locationgrant");
        this.claimTypeSelect.onChange = () => this.checkForm();
        this.checkForm();

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private checkForm() {
        if (this.claimTypeSelect.getValue() === claimTypes.locationGrant) {
            this.showLocationSelection();
        } else {
            this.$locationSelectionRow.hide();
        }
    }

    private showLocationSelection() {
        this.$locationSelectionRow.show();
        if (this.locationSelect !== undefined)
            this.locationSelect.destroy();

        this.locationSelect = new EntityInput({
            dataSource: Factory.getInstance<LocationDao>(LocationDao),
            takenIds: this.takenValues,
        }).renderTo(this.$locationSelectionRow.find("> .col-xl-6"));
    }

    private onSubmit() {
        const cmd: IUserGrantAccessCmd = {
            type: this.claimTypeSelect.getValue()!,
            value: this.locationSelect?.getValue()!,
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (cmd.type === undefined) {
            hasError = true;
            this.claimTypeSelect.setError(true);
        } else this.claimTypeSelect.setError(false);

        if (cmd.type == claimTypes.locationGrant && cmd.value === undefined) {
            hasError = true;
            this.locationSelect!.setError(true);
        } else {
            this.locationSelect!.setError(false);
}
        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<UserDao>(UserDao)
            .grantAccess(this.userId, cmd)
            .then((m) => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                this.close();
                if (this.onSaved !== undefined) this.onSaved(m);
            })
            .catch((ex) => {
                logError(ex);
                const e = ex as Error;
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn.find("> .la").length === 0) this.$submitBtn.prepend('<i class="la la-save" />');

                alert(i18n("modal.userAccess.failedEdit", { reason: e.message }));
            });
    }
}
